import React from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './MapboxMapView.css';

import DrawToolBar from '../../TerraDraw/DrawToolBar';
import { installGoogleCompat, MapboxMapShim, LatLng } from './googleCompat';

const STREETS_STYLE = 'mapbox://styles/mapbox/streets-v12';
const SATELLITE_STYLE = 'mapbox://styles/mapbox/satellite-streets-v12';

const BUILDINGS_SOURCE = 'geo-buildings';
const USERS_SOURCE = 'geo-users';
const BUILDINGS_LAYER = 'geo-buildings-layer';
const USERS_LAYER = 'geo-users-layer';
const FALLBACK_ICON = 'geo-fallback-pin';
const HIGHLIGHT_SCALE = 1.35;

const EMPTY_FC = { type: 'FeatureCollection', features: [] };

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Os ícones dos empreendimentos chegam como URL absoluta (url() do Laravel, com o host do
// APP_URL). O Mapbox só carrega imagem de outra origem se ela liberar CORS, então os
// ícones do próprio sistema (/img/...) são lidos pelo caminho, sempre na origem da página.
const normalizeIconUrl = (url) => {
    if (!url) return url;

    try {
        const { pathname } = new URL(url, window.location.href);

        return pathname.startsWith('/img/') ? pathname : url;
    } catch {
        return url;
    }
};

const iconUrl = (icon) => normalizeIconUrl(typeof icon === 'string' ? icon : icon?.url);

const parseLocation = (location) => (typeof location === 'string' ? JSON.parse(location) : location)?.coordinates;

// Pino vermelho usado quando o ícone do marcador não carrega.
const buildFallbackPin = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 28;
    canvas.height = 40;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#d9534f';
    ctx.beginPath();
    ctx.arc(14, 14, 12, Math.PI, 0);
    ctx.lineTo(14, 40);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(14, 14, 5, 0, Math.PI * 2);
    ctx.fill();

    return ctx.getImageData(0, 0, 28, 40);
};

/**
 * Mapa Mapbox do georreferenciamento. Mesma interface do MapWithADrawingManager (Google),
 * que continua em uso na rota legada: o restante da tela não muda, só o mapa.
 */
class MapboxMapView extends React.Component {
    containerRef = React.createRef();

    state = { ready: false, satellite: false };

    _buildings = [];

    _users = [];

    _renderToken = 0;

    _userMoved = false;

    componentDidMount() {
        installGoogleCompat();

        const token = window.mapboxApiKey;
        if (!token) {
            console.error('[MapboxMapView] window.mapboxApiKey ausente');
            return;
        }

        mapboxgl.accessToken = token;
        const { currentPosition } = this.props.state;

        this.map = new mapboxgl.Map({
            container: this.containerRef.current,
            style: STREETS_STYLE,
            center: [currentPosition.lng, currentPosition.lat],
            zoom: 14,
            // Necessário para a captura de tela ler os pixels do canvas WebGL.
            preserveDrawingBuffer: true,
        });

        this.shim = new MapboxMapShim(this.map);
        window.googleMap = this.shim;

        this.map.on('error', (e) => console.error('[MapboxMapView] erro:', e.error));
        this.map.on('dragstart', () => { this._userMoved = true; });
        this.map.on('zoomstart', (e) => { if (e.originalEvent) this._userMoved = true; });

        this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        this.map.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');
        this.map.addControl(this.buildStyleControl(), 'top-right');

        this.map.on('load', () => { this.initializeLayers(); this.setState({ ready: true }); });
        this.map.on('style.load', () => { if (this.state.ready) this.initializeLayers(); });
        this.map.on('click', (e) => this.shim.handleMapClick(e.lngLat));
    }

    componentDidUpdate(prevProps) {
        if (!this.map || !this.state.ready) return;

        const prev = prevProps.state;
        const next = this.props.state;

        this.shim.isDrawing = !!next.isDrawing;

        if (prev.currentPosition !== next.currentPosition && !this._userMoved) {
            this.map.jumpTo({ center: [next.currentPosition.lng, next.currentPosition.lat] });
        }

        if (
            prev.buildings !== next.buildings
            || prev.users !== next.users
            || prev.showMarkers !== next.showMarkers
            || prev.showBuildings !== next.showBuildings
            || prev.property_of_interest !== next.property_of_interest
        ) {
            this.renderMarkers();
        }

        // Formas restauradas (projeto salvo) nascem sem mapa: entram na primeira atualização.
        [...(next.manualCircles || []), ...(next.polygons || [])].forEach((shape) => {
            if (shape && typeof shape.setMap === 'function' && !shape._everAttached && !shape.getMap()) {
                shape.setMap(this.shim);
            }
        });
    }

    componentWillUnmount() {
        if (this.map) this.map.remove();
        if (window.googleMap === this.shim) delete window.googleMap;
    }

    /* ---------------- controles ---------------- */

    buildStyleControl() {
        const view = this;

        return {
            onAdd() {
                this._container = document.createElement('div');
                this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'geo-mb-style-btn';
                button.title = 'Alternar mapa / satélite';
                button.innerHTML = '<i class="fas fa-layer-group"></i>';
                button.onclick = () => {
                    const satellite = !view.state.satellite;
                    view.setState({ satellite });
                    view.map.setStyle(satellite ? SATELLITE_STYLE : STREETS_STYLE);
                };
                this._container.appendChild(button);

                return this._container;
            },
            onRemove() { this._container.remove(); },
        };
    }

    /* ---------------- camadas ---------------- */

    initializeLayers = () => {
        const map = this.map;

        if (!map.hasImage(FALLBACK_ICON)) map.addImage(FALLBACK_ICON, buildFallbackPin());

        this.shim.ensureShapeLayers();

        [BUILDINGS_SOURCE, USERS_SOURCE].forEach((id) => {
            if (!map.getSource(id)) map.addSource(id, { type: 'geojson', data: EMPTY_FC });
        });

        const layout = {
            'icon-image': ['get', 'icon'],
            'icon-size': ['get', 'scale'],
            'icon-anchor': 'bottom',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
        };

        if (!map.getLayer(BUILDINGS_LAYER)) {
            map.addLayer({ id: BUILDINGS_LAYER, type: 'symbol', source: BUILDINGS_SOURCE, layout });
            map.addLayer({ id: USERS_LAYER, type: 'symbol', source: USERS_SOURCE, layout });
        }

        // Os handlers por camada sobrevivem à troca de estilo: ligar uma vez só.
        if (!this._markerEventsBound) {
            this._markerEventsBound = true;
            this.bindMarkerEvents();
        }

        this.renderMarkers();
    };

    bindMarkerEvents() {
        const map = this.map;
        const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: [0, -34], className: 'geo-mb-popup' });

        const hover = (layer, html) => {
            map.on('mousemove', layer, (e) => {
                if (this.props.state.isDrawing) return;
                map.getCanvas().style.cursor = 'pointer';
                popup.setLngLat(e.features[0].geometry.coordinates).setHTML(html(e.features[0].properties)).addTo(map);
            });
            map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; popup.remove(); });
        };

        hover(BUILDINGS_LAYER, (p) => `<div><h5>${escapeHtml(p.nome)}</h5><small>
            <strong>Endereço: </strong>${escapeHtml(p.endereco)} ${escapeHtml(p.numero)}<br />
            <strong>Bairro: </strong>${escapeHtml(p.bairro)}</small></div>`);

        hover(USERS_LAYER, (p) => `<div><h5>${escapeHtml(p.user_type)}</h5><small>
            <strong>Endereço: </strong> ${escapeHtml(p.street)}<br />
            <strong>Bairro: </strong> ${escapeHtml(p.neighborhood)}</small></div>`);

        map.on('click', BUILDINGS_LAYER, (e) => {
            if (this.props.state.isDrawing) return;
            this.props.onMarkerClick(this._buildings[e.features[0].properties.idx]);
        });

        map.on('click', USERS_LAYER, (e) => {
            if (this.props.state.isDrawing) return;
            this.props.onMarkerClick(this._users[e.features[0].properties.idx]?.id);
        });
    }

    // Carrega (uma vez por URL) a imagem de cada ícone usado; falha cai no pino padrão.
    ensureIcon = (url) => new Promise((resolve) => {
        if (!url) { resolve(FALLBACK_ICON); return; }
        if (this.map.hasImage(url)) { resolve(url); return; }

        this.map.loadImage(url, (error, image) => {
            if (error || !image) { resolve(FALLBACK_ICON); return; }
            if (!this.map.hasImage(url)) this.map.addImage(url, image);
            resolve(url);
        });
    });

    renderMarkers = async () => {
        if (!this.map || !this.map.getSource(BUILDINGS_SOURCE)) return;

        const token = ++this._renderToken;
        const { state } = this.props;

        const buildings = (state.showMarkers && state.showBuildings) ? state.buildings : [];
        const users = state.showMarkers ? state.users : [];

        this._buildings = buildings;
        this._users = users;

        const buildingEntries = buildings.map((b, idx) => ({ item: b, idx, url: iconUrl(b.customMarker || b.marker) }));
        const userEntries = users.map((u, idx) => ({ item: u, idx, url: iconUrl(u.customMarker || u.marker) }));

        const urls = [...new Set([...buildingEntries, ...userEntries].map((e) => e.url).filter(Boolean))];
        const resolved = {};
        await Promise.all(urls.map(async (url) => { resolved[url] = await this.ensureIcon(url); }));

        // Uma renderização mais nova assumiu enquanto as imagens carregavam.
        if (token !== this._renderToken) return;

        const interest = state.property_of_interest || [];

        const buildingFeatures = buildingEntries
            .filter((e) => e.item.location?.coordinates)
            .map(({ item, idx, url }) => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [item.location.coordinates[0], item.location.coordinates[1]] },
                properties: {
                    idx,
                    icon: resolved[url] || FALLBACK_ICON,
                    scale: interest.includes(item.nome) ? HIGHLIGHT_SCALE : 1,
                    nome: item.nome ?? '',
                    endereco: item.endereco ?? '',
                    numero: item.numero ?? '',
                    bairro: item.bairro ?? '',
                },
            }));

        const userFeatures = userEntries
            .map(({ item, idx, url }) => {
                const coordinates = parseLocation(item.location);
                if (!coordinates) return null;

                return {
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: [coordinates[0], coordinates[1]] },
                    properties: {
                        idx,
                        icon: resolved[url] || FALLBACK_ICON,
                        scale: 1,
                        user_type: item.user_type ?? '',
                        street: item.street ?? '',
                        neighborhood: item.neighborhood ?? '',
                    },
                };
            })
            .filter(Boolean);

        this.map.getSource(BUILDINGS_SOURCE)?.setData({ type: 'FeatureCollection', features: buildingFeatures });
        this.map.getSource(USERS_SOURCE)?.setData({ type: 'FeatureCollection', features: userFeatures });
    };

    /* ---------------- desenho (mesmos callbacks do mapa Google) ---------------- */

    handleMarkerComplete = (marker) => {
        const position = marker.getPosition();
        this.shim.panTo(position);
        this.shim.setZoom(15);

        if (typeof this.props.onMarkerComplete === 'function') {
            this.props.onMarkerComplete(marker, this.shim);
        }
    };

    handleCircleComplete = (circle) => {
        circle.setOptions({ fillColor: this.props.getColor() });
        this.shim.panTo(circle.getCenter());

        window.circles.push(circle);
        this.props.onCircleComplete(circle);
        this.shim.setZoom(15);
    };

    handlePolygonComplete = (polygon) => {
        polygon.setOptions({ fillColor: this.props.getColor(), color: this.props.getColor() });
        this.props.onPolygonComplete(polygon);
    };

    handleRectangleComplete = (rectangle) => {
        rectangle.setOptions({ fillColor: this.props.getColor(), color: this.props.getColor() });
        this.props.onRectangleComplete(rectangle);
    };

    /* ---------------- busca de endereço ---------------- */

    onSearchKeyDown = (e) => {
        if (e.key !== 'Enter') return;
        const address = e.target.value.trim();
        if (!address) return;

        new window.google.maps.Geocoder().geocode({ address }, (results, status) => {
            if (status !== 'OK') return;
            this.shim.panTo(results[0].geometry.location);
            this.shim.setZoom(14);
        });
    };

    render() {
        const { hide_search_box } = this.props;

        return (
            <div className="geo-mb-container" style={{ minHeight: '400px', height: '90vh' }}>
                <div ref={this.containerRef} className="geo-mb-map" />

                {this.state.ready && !hide_search_box && (
                    <>
                        <DrawToolBar
                            onMarkerComplete={this.handleMarkerComplete}
                            onPolygonComplete={this.handlePolygonComplete}
                            onCircleComplete={this.handleCircleComplete}
                            onRectangleComplete={this.handleRectangleComplete}
                            setDrawing={this.props.setDrawing}
                        />
                        <input
                            type="text"
                            placeholder="Digite um endereço"
                            id="search_box"
                            className="geo-mb-search"
                            onKeyDown={this.onSearchKeyDown}
                        />
                    </>
                )}
            </div>
        );
    }
}

export default MapboxMapView;

export { LatLng };
